// SkatersView — every skater surface for the whole roster, instanced:
// dome, ground ring decal, direction fin, control node, tether, comet
// trail, charge arc, stun stars. One draw call per part regardless of
// roster size.
//
// Per-frame work is instance-matrix writes through module-scope scratch
// objects plus the aState attribute — zero allocation. Team colors are
// written once at construction.

import type { PlayerId } from "@arcade-hockey/shared";
import type { MatchSnapshot } from "@arcade-hockey/sim";
import { tuning } from "@arcade-hockey/sim";
import {
  Color,
  DynamicDrawUsage,
  Group,
  InstancedBufferAttribute,
  InstancedMesh,
  Matrix4,
  Quaternion,
  Vector3,
} from "three/webgpu";
import {
  buildDecalQuadGeometry,
  buildDomeGeometry,
  buildFinGeometry,
  buildNodeGeometry,
  buildStarGeometry,
  buildTetherGeometry,
  buildTrailGeometry,
} from "../geometry/entity-geometry.js";
import {
  type SkaterAttributes,
  type SkaterMaterials,
  applySkaterStyle,
  createSkaterMaterials,
} from "../materials/skater.js";
import { TEAM_ENTITY_COLORS } from "../style/palette.js";
import { type EntitiesStyle, entitiesStyle } from "../style/style-schema.js";
import { simToWorldInto, worldYawFromSimDir } from "../world-mapping.js";

type SkaterModule = typeof import("../materials/skater.js");

const M = new Matrix4();
const POS = new Vector3();
const QUAT = new Quaternion();
const SCALE = new Vector3();
const Y_AXIS = new Vector3(0, 1, 0);
const ZERO_SCALE = new Vector3(0, 0, 0);

const TETHER_START_WORLD = 0.22;
const TETHER_WIDTH = 0.05;
const STUN_STARS_PER = 4;
const STUN_RING_OMEGA = 0.005; // rad per ms
const STUN_STAR_RADIUS = 0.28;
const STUN_ORBIT_RADIUS = 0.75;

export class SkatersView {
  readonly root: Group;
  private readonly indexById = new Map<PlayerId, number>();
  private readonly attrs: SkaterAttributes;
  private materials: SkaterMaterials;
  private readonly dome: InstancedMesh;
  private readonly ring: InstancedMesh;
  private readonly fin: InstancedMesh;
  private readonly node: InstancedMesh;
  private readonly tether: InstancedMesh;
  private readonly trail: InstancedMesh;
  private readonly charge: InstancedMesh;
  private readonly stars: InstancedMesh;
  private readonly count: number;
  private readonly partList: ReadonlyArray<{
    readonly label: string;
    readonly mesh: InstancedMesh;
  }>;

  constructor(snap: MatchSnapshot) {
    this.root = new Group();
    this.count = snap.players.length;
    const n = this.count;

    this.attrs = {
      aCore: new InstancedBufferAttribute(new Float32Array(n * 3), 3),
      aGlow: new InstancedBufferAttribute(new Float32Array(n * 3), 3),
      aRim: new InstancedBufferAttribute(new Float32Array(n * 3), 3),
      aState: new InstancedBufferAttribute(new Float32Array(n * 4), 4),
    };
    this.attrs.aState.setUsage(DynamicDrawUsage);

    const color = new Color();
    for (let i = 0; i < n; i++) {
      const p = snap.players[i];
      if (p === undefined) continue;
      this.indexById.set(p.id, i);
      const team = TEAM_ENTITY_COLORS[p.teamId];
      color.set(team.core);
      this.attrs.aCore.setXYZ(i, color.r, color.g, color.b);
      color.set(team.glow);
      this.attrs.aGlow.setXYZ(i, color.r, color.g, color.b);
      color.set(team.rim);
      this.attrs.aRim.setXYZ(i, color.r, color.g, color.b);
    }

    this.materials = createSkaterMaterials(entitiesStyle, this.attrs);

    const make = (
      geo: ReturnType<typeof buildDomeGeometry>,
      material: SkaterMaterials[keyof SkaterMaterials],
      count = n,
      renderOrder = 0,
    ): InstancedMesh => {
      // Instanced attributes ride the geometry; each part gets its own
      // geometry instance wrapping the shared attribute objects.
      geo.setAttribute("aCore", this.attrs.aCore);
      geo.setAttribute("aGlow", this.attrs.aGlow);
      geo.setAttribute("aRim", this.attrs.aRim);
      geo.setAttribute("aState", this.attrs.aState);
      const mesh = new InstancedMesh(geo, material as never, count);
      mesh.instanceMatrix.setUsage(DynamicDrawUsage);
      mesh.frustumCulled = false;
      mesh.renderOrder = renderOrder;
      this.root.add(mesh);
      return mesh;
    };

    this.ring = make(buildDecalQuadGeometry(), this.materials.ring, n, 1);
    this.charge = make(buildDecalQuadGeometry(), this.materials.charge, n, 1);
    this.trail = make(buildTrailGeometry(), this.materials.trail, n, 2);
    this.tether = make(buildTetherGeometry(), this.materials.tether, n, 2);
    this.dome = make(buildDomeGeometry(), this.materials.dome, n, 3);
    this.fin = make(buildFinGeometry(), this.materials.fin, n, 4);
    this.node = make(buildNodeGeometry(), this.materials.node, n, 4);
    this.stars = make(buildStarGeometry(), this.materials.star, n * STUN_STARS_PER, 4);

    // Labelled handles to each instanced part, for devtools/object inspection (built once).
    this.partList = [
      { label: "dome", mesh: this.dome },
      { label: "ring", mesh: this.ring },
      { label: "fin", mesh: this.fin },
      { label: "node", mesh: this.node },
      { label: "tether", mesh: this.tether },
      { label: "trail", mesh: this.trail },
      { label: "charge", mesh: this.charge },
      { label: "stars", mesh: this.stars },
    ];
  }

  /** The instanced part meshes, labelled — for the object inspector. Order matches the render stack. */
  get parts(): ReadonlyArray<{ readonly label: string; readonly mesh: InstancedMesh }> {
    return this.partList;
  }

  applyStyle(s: EntitiesStyle): void {
    applySkaterStyle(this.materials.u, s);
  }

  hotSwap(mod: SkaterModule): void {
    const next = mod.createSkaterMaterials(entitiesStyle, this.attrs);
    const pairs: Array<[InstancedMesh, keyof SkaterMaterials]> = [
      [this.dome, "dome"],
      [this.ring, "ring"],
      [this.fin, "fin"],
      [this.node, "node"],
      [this.tether, "tether"],
      [this.trail, "trail"],
      [this.charge, "charge"],
      [this.stars, "star"],
    ];
    for (const [mesh, key] of pairs) {
      const old = mesh.material;
      mesh.material = next[key] as never;
      (old as { dispose(): void }).dispose();
    }
    this.materials = next;
  }

  update(snap: MatchSnapshot, controlledId: PlayerId | null, nowMs: number): void {
    const s = entitiesStyle;
    const playerRadius = tuning.player.radius;
    const domeR = playerRadius * s.skater.radiusFactor;
    const domeSquash = s.skater.domeSquash;
    const anchor = tuning.puck.controlAnchorDistance;

    for (const p of snap.players) {
      const i = this.indexById.get(p.id);
      if (i === undefined) continue;

      const yaw = worldYawFromSimDir(p.stickDir);
      simToWorldInto(POS, p.pos);

      // Dome.
      QUAT.identity();
      SCALE.set(domeR, domeR * domeSquash, domeR);
      M.compose(POS, QUAT, SCALE);
      this.dome.setMatrixAt(i, M);

      // Ground ring decal (also carries selection + protect annuli).
      SCALE.setScalar(domeR * 1.45);
      POS.y = 0.01;
      M.compose(POS, QUAT, SCALE);
      this.ring.setMatrixAt(i, M);

      // Charge arc decal, slightly outside the ring.
      SCALE.setScalar(domeR * 1.75);
      M.compose(POS, QUAT, SCALE);
      this.charge.setMatrixAt(i, M);

      // Fin: wedge at the stick direction, riding the dome edge.
      QUAT.setFromAxisAngle(Y_AXIS, yaw);
      SCALE.set(domeR * 0.8, domeR * 0.8, domeR * s.skater.finLengthFrac);
      POS.y = 0.02;
      M.compose(POS, QUAT, SCALE);
      this.fin.setMatrixAt(i, M);

      // Tether + control node along the stick direction.
      const nodeR = playerRadius * 0.28;
      SCALE.set(TETHER_WIDTH, 1, Math.max(0.001, anchor - TETHER_START_WORLD));
      simToWorldInto(POS, p.pos, 0.04);
      POS.x += p.stickDir.y * TETHER_START_WORLD;
      POS.z += -p.stickDir.x * TETHER_START_WORLD;
      M.compose(POS, QUAT, SCALE);
      this.tether.setMatrixAt(i, M);

      simToWorldInto(POS, p.pos, nodeR + 0.02);
      POS.x += p.stickDir.y * anchor;
      POS.z += -p.stickDir.x * anchor;
      SCALE.setScalar(nodeR);
      M.compose(POS, QUAT, SCALE);
      this.node.setMatrixAt(i, M);

      // Comet trail: opposite velocity, length = speed × seconds.
      const speed = Math.hypot(p.vel.x, p.vel.y);
      if (speed >= s.trail.minSpeed) {
        const trailYaw = Math.atan2(-p.vel.y, p.vel.x); // opposite of velocity
        QUAT.setFromAxisAngle(Y_AXIS, trailYaw);
        SCALE.set(domeR * 0.9, 1, speed * s.trail.seconds);
        simToWorldInto(POS, p.pos, 0.015);
        M.compose(POS, QUAT, SCALE);
      } else {
        M.compose(POS, QUAT, ZERO_SCALE);
      }
      this.trail.setMatrixAt(i, M);

      // Stun stars orbiting above the dome.
      const stunned = p.action === "Stunned";
      const baseAngle = nowMs * STUN_RING_OMEGA;
      for (let k = 0; k < STUN_STARS_PER; k++) {
        const starIdx = i * STUN_STARS_PER + k;
        if (stunned) {
          const a = baseAngle + (k / STUN_STARS_PER) * Math.PI * 2;
          simToWorldInto(POS, p.pos, domeR * domeSquash + 0.25);
          POS.x += Math.cos(a) * STUN_ORBIT_RADIUS;
          POS.z += Math.sin(a) * STUN_ORBIT_RADIUS;
          QUAT.setFromAxisAngle(Y_AXIS, a);
          SCALE.setScalar(STUN_STAR_RADIUS);
          M.compose(POS, QUAT, SCALE);
        } else {
          M.compose(POS, QUAT, ZERO_SCALE);
        }
        this.stars.setMatrixAt(starIdx, M);
      }

      // Per-frame state attribute.
      const chargeFraction = Math.min(1, p.shootHoldTicks / tuning.shot.chargeMaxTicks);
      const trailAlpha =
        speed >= s.trail.minSpeed ? Math.min(s.trail.maxAlpha, 0.12 + speed * 0.04) : 0;
      this.attrs.aState.setXYZW(
        i,
        p.id === controlledId ? 1 : 0,
        p.isProtecting ? 1 : 0,
        chargeFraction,
        trailAlpha,
      );
    }

    this.dome.instanceMatrix.needsUpdate = true;
    this.ring.instanceMatrix.needsUpdate = true;
    this.charge.instanceMatrix.needsUpdate = true;
    this.fin.instanceMatrix.needsUpdate = true;
    this.node.instanceMatrix.needsUpdate = true;
    this.tether.instanceMatrix.needsUpdate = true;
    this.trail.instanceMatrix.needsUpdate = true;
    this.stars.instanceMatrix.needsUpdate = true;
    this.attrs.aState.needsUpdate = true;
  }
}

// One live view per page for material HMR (same pattern as RinkView).
let activeSkatersView: SkatersView | null = null;
export function setActiveSkatersView(view: SkatersView): void {
  activeSkatersView = view;
}

if (import.meta.hot) {
  import.meta.hot.accept("../materials/skater.ts", (m) => {
    if (m !== undefined) activeSkatersView?.hotSwap(m as unknown as SkaterModule);
  });
}
